import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../context/NotificationContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import umService from "../../services/university-admin/umService";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const UMEventTicketAttendants = ({ event }) => {
  const { t } = useTranslation(["universityManager", "common"]);
  const { showSuccess, showError } = useNotification();
  const { can } = useUniAdminAuth();
  const canAdd = can(PERMISSIONS.event.attendantAdd);
  const canRemove = can(PERMISSIONS.event.attendantRemove);

  const [attendants, setAttendants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);

  const [pendingEmails, setPendingEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");

  const fetchAttendants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await umService.getTicketAttendants(event.id);
      setAttendants(response.success ? response.data || [] : []);
    } catch (error) {
      console.error("Error fetching ticket attendants:", error);
      setAttendants([]);
    } finally {
      setLoading(false);
    }
  }, [event.id]);

  useEffect(() => {
    fetchAttendants();
  }, [fetchAttendants]);

  const handleAddPending = () => {
    const email = emailInput.trim();
    if (!email) return;
    if (!EMAIL_REGEX.test(email)) {
      showError(t("events.ticketAttendants.invalidEmail"));
      return;
    }
    const alreadyExists = attendants.some((a) => a.email === email) || pendingEmails.includes(email);
    if (alreadyExists) {
      setEmailInput("");
      return;
    }
    setPendingEmails((prev) => [...prev, email]);
    setEmailInput("");
  };

  const handleRemovePending = (email) => {
    setPendingEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleSubmitPending = async () => {
    if (pendingEmails.length === 0) return;
    try {
      setSubmitting(true);
      const response = await umService.addTicketAttendants(event.id, pendingEmails);
      if (response.success) {
        showSuccess(t("events.ticketAttendants.addSuccess"));
        setPendingEmails([]);
        await fetchAttendants();
      } else {
        showError(t("events.ticketAttendants.addFailed"));
      }
    } catch (error) {
      console.error("Error adding ticket attendants:", error);
      showError(t("events.ticketAttendants.addFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (member) => {
    if (!window.confirm(t("events.ticketAttendants.confirmRemove"))) return;
    try {
      setRemovingEmail(member.email);
      const response = await umService.removeTicketAttendant(event.id, member.email);
      if (response.success) {
        showSuccess(t("events.ticketAttendants.removeSuccess"));
        await fetchAttendants();
      } else {
        showError(t("events.ticketAttendants.removeFailed"));
      }
    } catch (error) {
      console.error("Error removing ticket attendant:", error);
      showError(t("events.ticketAttendants.removeFailed"));
    } finally {
      setRemovingEmail(null);
    }
  };

  return (
    <div className="event-ticket-attendants">
      <p className="text-muted small mb-3">
        {event.isUniversityEvent
          ? t("events.ticketAttendants.universityEventNote")
          : t("events.ticketAttendants.clubEventNote")}
      </p>

      {canAdd && (
        <div className="card border-0 bg-light mb-4">
          <div className="card-body">
            <label className="form-label fw-bold">{t("events.ticketAttendants.addEmail")}</label>
            <div className="row g-2 align-items-center">
              <div className="col-md-8">
                <input
                  type="email"
                  className="form-control"
                  placeholder={t("events.ticketAttendants.emailPlaceholder")}
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddPending();
                    }
                  }}
                />
              </div>
              <div className="col-md-4">
                <button type="button" className="btn btn-outline-primary w-100" onClick={handleAddPending}>
                  <i className="fas fa-plus me-1"></i>
                  {t("events.ticketAttendants.add")}
                </button>
              </div>
            </div>

            {pendingEmails.length > 0 && (
              <>
                <ul className="list-group mt-3">
                  {pendingEmails.map((email) => (
                    <li key={email} className="list-group-item d-flex justify-content-between align-items-center">
                      {email}
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleRemovePending(email)}>
                        <i className="fas fa-times"></i>
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="btn btn-success btn-sm mt-3"
                  onClick={handleSubmitPending}
                  disabled={submitting}
                >
                  {submitting ? (
                    <span className="spinner-border spinner-border-sm me-1"></span>
                  ) : (
                    <i className="fas fa-check me-1"></i>
                  )}
                  {t("events.ticketAttendants.submit")}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <h6 className="fw-bold mb-3">{t("events.ticketAttendants.title")}</h6>

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : attendants.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="bg-light">
              <tr>
                <th className="fw-bold">{t("events.ticketAttendants.columns.name")}</th>
                <th className="fw-bold">{t("events.ticketAttendants.columns.email")}</th>
                <th className="fw-bold text-center">{t("crud.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {attendants.map((member) => (
                <tr key={member.membershipId || member.email}>
                  <td>{member.name}</td>
                  <td className="text-muted">{member.email}</td>
                  <td className="text-center">
                    {canRemove && (
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleRemove(member)}
                        disabled={removingEmail === member.email}
                        title={t("events.ticketAttendants.remove")}
                      >
                        {removingEmail === member.email ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="fas fa-user-minus"></i>
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-4">
          <i className="fas fa-id-badge text-muted mb-2" style={{ fontSize: "2rem" }}></i>
          <p className="text-muted mb-0">{t("events.ticketAttendants.noAttendants")}</p>
        </div>
      )}
    </div>
  );
};

export default UMEventTicketAttendants;
